import React, { useState, useMemo } from "react";
import TopNav from "../Navbars/TopNav";
import Sidebar from "../Navbars/sidebar";
import {
  useGetAssessmentDetails,
  UseUserinfo,
  useEndorseAssessment,
  useGetInternByid,
} from "../../adapters/Requests";
import { useParams, useNavigate, Link } from "react-router-dom";
import BankUpdateModal from "../modals/BankUpdateModal";
import { toast } from "sonner";
import {
  CheckCircle,
  FileText,
  User,
  Warning,
  PencilSimple,
} from "@phosphor-icons/react";
import { Home, ChevronRight, ArrowLeft } from "lucide-react";

function EndorseReport() {
  const { id: internId } = useParams();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [parsedBankRequest, setParsedBankRequest] = useState(null);

  const { data: userData } = UseUserinfo();
  const { mutateAsync: endorseAssessment } = useEndorseAssessment();
  const { data: internData, refetch: refetchIntern } = useGetInternByid(internId);

  function getCurrentMonthYear() {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const now = new Date();
    return `${months[now.getMonth()]} ${now.getFullYear()}`;
  }

  const { data: assessmentResponse } = useGetAssessmentDetails({
    intern_id: internId,
    month_year: getCurrentMonthYear(),
  });
  const assessment = assessmentResponse?.current || {};

  const application = internData?.application || {};
  const placement = internData?.InternPlacements?.[0];
  const internshipProgram = application?.Applicant?.InternshipProgram?.[0];
  React.useEffect(() => {
    if (
      assessment?.supervisors_comment?.includes(
        "[URGENT: BANK DETAILS UPDATE REQUESTED]"
      )
    ) {
      const match = assessment.supervisors_comment.match(
        /BANK_NAME:(.*?)\|\|ACC_NO:(.*?)\|\|SORT:(.*?)(?:\n|$)/
      );
      if (match) {
        setParsedBankRequest({
          bankName: match[1],
          accountNumber: match[2],
          sortCode: match[3],
        });
      }
    }
  }, [assessment]);

  const isBankUpdated = useMemo(() => {
    const currentBank = internData?.InternBankAccounts?.[0];
    if (!parsedBankRequest || !currentBank) return false;

    return String(currentBank.account_number).trim() === String(parsedBankRequest.accountNumber).trim();
  }, [parsedBankRequest, internData]);

  const calculateDuration = () => {
    if (internshipProgram?.duration_in_months) {
      const map = {
        three_months: "3 Months",
        four_months: "4 Months",
        five_months: "5 Months",
        six_months: "6 Months",
        one_year: "1 Year",
      };
      return map[internshipProgram.duration_in_months] || "N/A";
    }

    if (placement?.actual_resumption_date && placement?.closing_date) {
      const start = new Date(placement.actual_resumption_date);
      const end = new Date(placement.closing_date);

      let months = (end.getFullYear() - start.getFullYear()) * 12;
      months -= start.getMonth();
      months += end.getMonth();

      if (end.getDate() < start.getDate()) months--;

      if (months <= 3) return "3 Months";
      if (months <= 4) return "4 Months";
      if (months <= 5) return "5 Months";
      if (months <= 6) return "6 Months";
      return "1 Year";
    }

    return "N/A";
  };

  const formattedDuration = calculateDuration();

  const cleanComment = (rawComment) => {
    if (!rawComment) return "No remarks provided.";
    return rawComment
      .replace("[URGENT: BANK DETAILS UPDATE REQUESTED]", "")
      .replace(/---[\s\S]*$/, "")
      .replace(/Endorsed by:[\s\S]*$/, "")
      .replace(/BANK_NAME:[\s\S]*$/, "")
      .trim();
  };

  const getSupervisorFromComment = (comment) => {
    if (!comment) return "N/A";
    const match = comment.match(/Endorsed by: (.*?)(?:\(|$|\n)/);
    return match ? match[1].trim() : "N/A";
  };

  const supervisorName = getSupervisorFromComment(
    assessment.supervisors_comment
  );

  const department =
    internshipProgram?.department ||
    application?.vacancy?.business_unit?.name ||
    "N/A";

  const institutionName =
    application?.ApplicationsAcademicQualification?.institution?.name || "N/A";

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const handleEndorse = async () => {
    if (!assessment.id) {
      toast.error("No assessment record found.");
      return;
    }

    setIsSubmitting(true);
    try {
      await endorseAssessment({
        assessment_id: assessment.id,
        manager_details: {
          name: `${userData.first_name} ${userData.last_name}`,
          designation: userData.business_unit?.name || "Manager",
        },
      });

      toast.success("Report Endorsed & Generated Successfully!");
      setTimeout(() => navigate(-1), 2000);
    } catch (error) {
      console.error(error);
      toast.error("Endorsement Failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const ReportField = ({ label, value, highlight = false }) => (
    <div className="flex flex-col border-b border-slate-100 pb-2 last:border-0">
      <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-1">
        {label}
      </span>
      <span
        className={`text-sm ${highlight ? "font-bold text-slate-800" : "font-medium text-slate-600"
          }`}
      >
        {value || "N/A"}
      </span>
    </div>
  );

  return (
    <>
      <div className="flex h-screen overflow-hidden bg-slate-50">
        <div className="relative flex-1 flex flex-col overflow-y-auto overflow-x-hidden">
          <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Link
                  to="/dashboard"
                  className="flex items-center hover:text-green-600 transition-colors"
                >
                  <Home size={16} />
                </Link>
                <ChevronRight size={16} className="text-gray-300" />
                <span className="font-semibold text-gray-900">
                  Endorse Report
                </span>
              </div>

              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft size={16} /> Back
              </button>
            </div>
          </div>

          <main className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center mb-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center gap-2 mb-2">
                  <FileText size={24} className="text-slate-400" />
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                    Official Document
                  </span>
                </div>
                <h1 className="text-2xl font-black text-slate-800 uppercase tracking-tight">
                  {internData?.first_name
                    ? `${internData.first_name}'S ASSESSMENT REPORT`
                    : "ASSESSMENT REPORT"}
                </h1>
                <p className="text-sm text-green-600 font-bold mt-1 uppercase tracking-wide">
                  {getCurrentMonthYear()}
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-6 py-3 border-b border-slate-100 flex items-center gap-2">
                  <User size={16} className="text-slate-500" />
                  <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Intern Profile
                  </h2>
                </div>
                <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
                  <ReportField
                    label="Full Name"
                    value={`${internData?.first_name || ""} ${internData?.last_name || ""
                      }`}
                    highlight
                  />
                  <ReportField
                    label="Matric Number"
                    value={
                      application?.ApplicationsAcademicQualification
                        ?.matric_number || "N/A"
                    }
                  />
                  <ReportField label="Institution" value={institutionName} />
                  <ReportField label="Duration" value={formattedDuration} />
                  <ReportField
                    label="Start Date"
                    value={formatDate(placement?.actual_resumption_date)}
                  />
                  <ReportField
                    label="End Date"
                    value={formatDate(placement?.closing_date)}
                  />
                  <ReportField label="Department" value={department} />
                  <ReportField
                    label="Unit"
                    value={application.vacancy?.business_unit?.name || "N/A"}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-6 py-3 border-b border-slate-100">
                    <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                      Attendance Summary
                    </h2>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center p-3 rounded-lg bg-slate-50 border border-slate-100">
                        <div className="text-2xl font-bold text-slate-700">
                          {internData?.MonthlyAttendances?.[0]?.total_days || 0}
                        </div>
                        <div className="text-[9px] uppercase font-bold text-slate-400 mt-1">
                          Expected
                        </div>
                      </div>
                      <div className="text-center p-3 rounded-lg bg-green-50 border border-green-100">
                        <div className="text-2xl font-bold text-green-700">
                          {internData?.MonthlyAttendances?.[0]?.days_present ||
                            0}
                        </div>
                        <div className="text-[9px] uppercase font-bold text-green-600 mt-1">
                          Present
                        </div>
                      </div>
                      <div className="text-center p-3 rounded-lg bg-red-50 border border-red-100">
                        <div className="text-2xl font-bold text-red-700">
                          {internData?.MonthlyAttendances?.[0]?.days_absent ||
                            0}
                        </div>
                        <div className="text-[9px] uppercase font-bold text-red-600 mt-1">
                          Absent
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-6 py-3 border-b border-slate-100">
                    <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                      Performance Ratings
                    </h2>
                  </div>
                  <div className="p-6 grid grid-cols-2 gap-x-6 gap-y-4">
                    <ReportField
                      label="Punctuality"
                      value={assessment.punctuality_score}
                      highlight
                    />
                    <ReportField
                      label="Dedication"
                      value={assessment.dedication_to_duty_score}
                      highlight
                    />
                    <ReportField
                      label="Safety"
                      value={assessment.safety_conduct_score}
                      highlight
                    />
                    <ReportField
                      label="Conduct"
                      value={assessment.general_conduct_score}
                      highlight
                    />
                    <ReportField
                      label="Ability"
                      value={assessment.ability_to_learn_score}
                      highlight
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-6 py-3 border-b border-slate-100">
                  <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Official Endorsement
                  </h2>
                </div>
                <div className="p-6">
                  {parsedBankRequest &&
                    !assessment.supervisors_comment?.includes(
                      "[TMD ENDORSED]"
                    ) && (
                      <div className={`mb-6 border rounded-lg p-4 transition-all ${isBankUpdated ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-full ${isBankUpdated ? 'bg-green-100 text-green-600' : 'bg-orange-100 text-orange-600'}`}>
                            {isBankUpdated ? <CheckCircle size={20} weight="fill" /> : <Warning size={20} weight="fill" />}
                          </div>
                          <div className="flex-1">
                            <h3 className={`text-sm font-bold mb-1 ${isBankUpdated ? 'text-green-900' : 'text-orange-900'}`}>
                              {isBankUpdated ? "Bank Details Updated Successfully" : "Supervisor Requested Bank Change"}
                            </h3>
                            <p className={`text-xs mb-3 ${isBankUpdated ? 'text-green-800' : 'text-orange-800'}`}>
                              {isBankUpdated
                                ? "The requested changes have been applied to the intern's profile."
                                : "Please review and apply these changes before endorsing."}
                            </p>

                            {!isBankUpdated && (
                              <>
                                <div className="grid grid-cols-2 gap-2 mb-3 bg-white p-3 rounded border border-orange-100">
                                  <div>
                                    <span className="text-[10px] text-slate-400 uppercase font-bold">
                                      Suggested Bank
                                    </span>
                                    <p className="font-bold text-slate-700">
                                      {parsedBankRequest.bankName}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-[10px] text-slate-400 uppercase font-bold">
                                      Suggested Account
                                    </span>
                                    <p className="font-bold text-slate-700">
                                      {parsedBankRequest.accountNumber}
                                    </p>
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => setIsBankModalOpen(true)}
                                  className="text-xs bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-md font-bold transition-colors flex items-center gap-2"
                                >
                                  <PencilSimple size={14} />
                                  Update Bank Details Now
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                  <div className="mb-6">
                    <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider block mb-2">
                      Supervisor Remarks
                    </span>
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-sm text-slate-700 italic leading-relaxed">
                      "{cleanComment(assessment.supervisors_comment)}"
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8 border-t border-slate-100 pt-6">
                    <ReportField label="Supervised By" value={supervisorName} />
                    <ReportField
                      label="Endorsing Manager"
                      value={`${userData?.first_name || ""} ${userData?.last_name || ""
                        }`}
                      highlight
                    />
                  </div>

                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={handleEndorse}
                      disabled={
                        isSubmitting ||
                        !assessment.id ||
                        assessment.supervisors_comment?.includes(
                          "[TMD ENDORSED]"
                        )
                      }
                      className={`
                        w-full py-4 px-4
                        inline-flex justify-center items-center gap-2 
                        rounded-lg font-bold text-sm tracking-wide transition-all
                        ${assessment.supervisors_comment?.includes(
                        "[TMD ENDORSED]"
                      )
                          ? "bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200"
                          : "bg-green-600 text-white hover:bg-green-700 shadow-xl shadow-green-600/20 active:scale-[0.99]"
                        }
                      `}
                    >
                      {isSubmitting ? (
                        "Processing..."
                      ) : assessment.supervisors_comment?.includes(
                        "[TMD ENDORSED]"
                      ) ? (
                        <>
                          <CheckCircle size={18} weight="fill" /> Report
                          Endorsed
                        </>
                      ) : (
                        "ENDORSE "
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      <BankUpdateModal
        isOpen={isBankModalOpen}
        onClose={() => setIsBankModalOpen(false)}
        intern={internData}
        initialValues={parsedBankRequest}
        onSuccess={() => {
          toast.success("Bank details updated!");
          refetchIntern();
        }}
      />


    </>
  );
}

export default EndorseReport;